import { Link } from "react-router-dom";
import { useSeo } from "@/components/Seo";
import { CountUp } from "@/components/CountUp";
import { Reveal } from "@/components/Reveal";
import { ProjectCard } from "@/components/ProjectCard";
import { STATS, PILLARS, PROJECTS, DIRECTORS, PUNE_MARKETS } from "@/data/content";

export default function HomePage() {
  useSeo({
    title: "Jaybhaye Realty Pvt. Ltd. | Real Estate Developers in Aurangabad & Pune",
    description:
      "Jaybhaye Realty Pvt. Ltd. is a new-generation real estate development company carrying forward the 15+ year legacy of Apratim Group / Apratim Builders — 900+ homes delivered across Chhatrapati Sambhaji Nagar, now expanding into Pune. Growing with Integrity.",
    path: "/",
    keywords:
      "Jaybhaye Realty, Jaybhaye Realty Pune, Jaybhaye Realty Aurangabad, Apratim Group, Apratim Builders, Apratim Group Aurangabad",
  });

  return (
    <div data-testid="home-page">
      {/* HERO — type-led cinematic declaration */}
      <section className="py-24 lg:py-32 border-b border-[#12281a]/15 bg-[#f7f5f0]" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-5xl">
            <span className="text-xs uppercase tracking-[0.3em] text-[#b38b47] font-bold block mb-6">
              Jaybhaye Realty Pvt. Ltd. · Estd. 2022
            </span>
            <h1 className="font-monument text-5xl sm:text-7xl lg:text-8xl text-[#12281a] leading-[0.98] tracking-tight uppercase mb-8" data-testid="hero-headline">
              Building Spaces. Creating Trust.
            </h1>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start pt-6 border-t border-[#12281a]/20">
              <div className="md:col-span-8">
                <p className="text-xl sm:text-2xl text-[#2c382f] font-light leading-snug">
                  A new-generation real estate development company carrying forward the legacy of Apratim Group —
                  thoughtfully planned residential, commercial and plotted developments across Maharashtra.
                </p>
                <div className="flex flex-wrap gap-4 mt-8">
                  <Link
                    to="/projects"
                    data-testid="hero-explore-projects-button"
                    className="px-8 py-3.5 bg-[#12281a] hover:bg-[#1f422b] text-[#f7f5f0] text-xs uppercase tracking-widest font-semibold transition-colors"
                  >
                    Explore Our Projects
                  </Link>
                  <Link
                    to="/about"
                    data-testid="hero-our-story-button"
                    className="px-8 py-3.5 border border-[#12281a]/30 hover:border-[#b38b47] text-[#12281a] text-xs uppercase tracking-widest font-semibold transition-colors"
                  >
                    Our Story
                  </Link>
                </div>
              </div>
              <div className="md:col-span-4 flex flex-col justify-end">
                <div className="text-xs uppercase tracking-widest text-[#12281a]/60 mb-2">Corporate Principle</div>
                <div className="text-lg font-body-serif italic text-[#12281a] font-semibold" data-testid="hero-positioning">
                  Growing with Integrity.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CREDIBILITY NUMBERS */}
      <section className="bg-[#12281a] text-[#f7f5f0]" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 grid grid-cols-2 lg:grid-cols-4 gap-10">
          {STATS.map((s, i) => (
            <div key={s.label} className="border-l-2 border-[#b38b47]/40 pl-6">
              <div className="font-monument text-5xl sm:text-6xl text-[#b38b47]">
                <CountUp end={s.value} suffix={s.suffix} testId={`stat-value-${i}`} />
              </div>
              <div className="text-xs uppercase tracking-widest text-[#f7f5f0]/70 mt-3">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* MANIFESTO PREVIEW */}
      <section className="py-24 bg-white border-b border-[#12281a]/10" data-testid="manifesto-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Reveal>
            <div className="mb-16 max-w-2xl">
              <span className="text-xs uppercase tracking-[0.25em] text-[#b38b47] font-semibold block mb-2">
                Why Jaybhaye Realty
              </span>
              <h2 className="text-3xl sm:text-4xl font-monument text-[#12281a]">
                Built on Experience. Driven by the Next Generation.
              </h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {PILLARS.slice(0, 3).map((p, i) => (
              <Reveal key={p.title} delay={i * 120}>
                <div className="border-t border-[#12281a]/20 pt-6">
                  <div className="chapter-num mb-2">{p.numeral}</div>
                  <h3 className="text-lg font-monument text-[#12281a] mb-2 font-semibold">{p.title}</h3>
                  <p className="text-xs text-[#2c382f]/80 leading-relaxed">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={200}>
            <Link
              to="/about"
              data-testid="manifesto-more-link"
              className="inline-block mt-12 text-xs uppercase tracking-widest font-semibold text-[#12281a] border-b-2 border-[#b38b47] pb-1 hover:text-[#b38b47] transition-colors"
            >
              Our Full Story →
            </Link>
          </Reveal>
        </div>
      </section>

      {/* LINEAGE PREVIEW */}
      <section className="py-24 bg-[#12281a] text-[#f7f5f0]" data-testid="lineage-preview-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Reveal>
            <div className="max-w-3xl mb-16">
              <span className="text-xs uppercase tracking-[0.25em] text-[#b38b47] font-semibold block mb-2">
                Our Legacy
              </span>
              <h2 className="text-3xl sm:text-4xl font-monument text-[#f7f5f0]">From Foundation to Future</h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <Reveal>
              <div className="border-l-2 border-[#5c1822] pl-8">
                <span className="text-xs uppercase tracking-[0.2em] text-[#b38b47] font-semibold block">The Foundation</span>
                <h3 className="text-2xl font-body-serif text-[#f7f5f0] mt-1 mb-4">Apratim Group / Apratim Builders</h3>
                <p className="text-sm text-[#f7f5f0]/70 leading-relaxed mb-6">
                  Pioneered residential communities in Chhatrapati Sambhaji Nagar (Aurangabad) — flagship delivered
                  developments including Apratim Radhai Estate, Apratim Park, Apratim Vastu and Apratim Pushp.
                </p>
                <div className="flex gap-6 text-xs text-[#f7f5f0]/50 uppercase tracking-wider">
                  <span>15+ Acres</span>
                  <span>·</span>
                  <span>900+ Homes</span>
                </div>
              </div>
            </Reveal>
            <Reveal delay={150}>
              <div className="border-l-2 border-[#b38b47] pl-8">
                <span className="text-xs uppercase tracking-[0.2em] text-[#b38b47] font-semibold block">The Next Generation</span>
                <h3 className="text-2xl font-body-serif text-[#f7f5f0] mt-1 mb-4">Jaybhaye Realty Pvt. Ltd.</h3>
                <p className="text-sm text-[#f7f5f0]/70 leading-relaxed mb-6">
                  Incorporated in 2022 to carry that foundation into Pune's premier urban markets — combining
                  generational wisdom with contemporary development standards.
                </p>
                <div className="flex gap-6 text-xs text-[#b38b47] uppercase tracking-wider font-semibold">
                  <span>New Markets</span>
                  <span>·</span>
                  <span>New Possibilities</span>
                </div>
              </div>
            </Reveal>
          </div>
          <Reveal delay={250}>
            <Link
              to="/legacy"
              data-testid="legacy-more-link"
              className="inline-block mt-12 text-xs uppercase tracking-widest font-semibold text-[#b38b47] border-b-2 border-[#b38b47]/50 pb-1 hover:text-[#f7f5f0] transition-colors"
            >
              Explore Our Journey →
            </Link>
          </Reveal>
        </div>
      </section>

      {/* SELECTED PROJECTS */}
      <section className="py-24" data-testid="projects-preview-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 pb-4 border-b border-[#12281a]/15">
              <div>
                <span className="text-xs uppercase tracking-[0.25em] text-[#b38b47] font-semibold block mb-2">Portfolio</span>
                <h2 className="text-3xl sm:text-4xl font-monument text-[#12281a]">Selected Projects</h2>
              </div>
              <span className="text-xs text-[#12281a]/60 uppercase tracking-widest mt-2 md:mt-0">
                Delivered by Apratim Group
              </span>
            </div>
          </Reveal>
          <div className="space-y-8">
            {PROJECTS.slice(0, 3).map((p, i) => (
              <Reveal key={p.slug} delay={i * 100}>
                <ProjectCard project={p} index={i} />
              </Reveal>
            ))}
          </div>
          <Reveal delay={200}>
            <Link
              to="/projects"
              data-testid="projects-more-link"
              className="inline-block mt-12 text-xs uppercase tracking-widest font-semibold text-[#12281a] border-b-2 border-[#b38b47] pb-1 hover:text-[#b38b47] transition-colors"
            >
              View All Projects →
            </Link>
          </Reveal>
        </div>
      </section>

      {/* PUNE EXPANSION */}
      <section className="py-24 bg-[#f0ece1]" data-testid="pune-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <Reveal>
                <span className="text-xs uppercase tracking-[0.25em] text-[#b38b47] font-semibold block mb-2">
                  Expansion
                </span>
                <h2 className="text-3xl sm:text-4xl font-monument text-[#12281a] mb-6">
                  From Aurangabad to Pune.
                </h2>
                <p className="text-base text-[#2c382f] leading-relaxed mb-8">
                  Jaybhaye Realty brings its 15-year Sambhaji Nagar track record to key growth belts across Pune —
                  actively collaborating with discerning landowners and joint-development partners.
                </p>
              </Reveal>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {PUNE_MARKETS.map((m, i) => (
                  <Reveal key={m.name} delay={i * 80}>
                    <div className="bg-white p-4 border border-[#12281a]/15 h-full" data-testid={`pune-market-${m.name.toLowerCase().replace(/ /g, "-")}`}>
                      <span className="text-xs uppercase tracking-wider text-[#12281a] font-bold block">{m.name}</span>
                      <span className="text-[11px] text-[#12281a]/60">{m.note}</span>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={150}>
                <div className="aspect-square bg-gradient-to-br from-[#12281a] to-[#254631] text-[#f7f5f0] p-8 flex flex-col justify-between border border-[#b38b47]/30 shadow-md" data-testid="maharashtra-axis-card">
                  <div>
                    <span className="text-[10px] uppercase tracking-widest text-[#b38b47]">State Strategic Axis</span>
                    <div className="text-2xl font-monument mt-1">Maharashtra Footprint</div>
                  </div>
                  <div className="space-y-4 my-auto">
                    <div className="border-b border-[#f7f5f0]/10 pb-3">
                      <span className="text-xs text-[#b38b47] uppercase tracking-wider">Foundation Anchor</span>
                      <div className="text-base font-body-serif">Chhatrapati Sambhaji Nagar</div>
                      <div className="text-xs text-[#f7f5f0]/60">900+ Families · 15+ Acres</div>
                    </div>
                    <div className="pt-1">
                      <span className="text-xs text-[#b38b47] uppercase tracking-wider">Growth Frontier</span>
                      <div className="text-base font-body-serif">Pune Metropolitan Belt</div>
                      <div className="text-xs text-[#f7f5f0]/60">West & South Expansion</div>
                    </div>
                  </div>
                  <div className="text-[11px] text-[#f7f5f0]/50 uppercase tracking-wider">
                    Estd. 2022 · Jaybhaye Realty Pvt. Ltd.
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* LEADERSHIP PREVIEW */}
      <section className="py-24" data-testid="leadership-preview-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-16">
              <span className="text-xs uppercase tracking-[0.25em] text-[#b38b47] font-semibold block mb-2">
                Leadership
              </span>
              <h2 className="text-3xl sm:text-4xl font-monument text-[#12281a]">Board of Directors</h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-4xl mx-auto">
            {DIRECTORS.map((d, i) => (
              <Reveal key={d.initials} delay={i * 120}>
                <Link
                  to="/about"
                  className="group block border border-[#12281a]/20 bg-white h-full hover:border-[#b38b47]/50 transition-colors"
                  data-testid={`director-card-${d.initials.toLowerCase()}`}
                >
                  <div className="aspect-[4/5] overflow-hidden">
                    <img
                      src={d.photo}
                      alt={`${d.name}, ${d.role}`}
                      className="w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform duration-700"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-8">
                    <span className="text-[11px] uppercase tracking-[0.2em] text-[#b38b47] font-semibold block mb-2">
                      {d.tagline}
                    </span>
                    <h3 className="text-xl font-monument text-[#12281a]">{d.name}</h3>
                    <span className="text-xs uppercase tracking-widest text-[#12281a]/60 font-semibold block mt-1 mb-4">
                      {d.role}
                    </span>
                    <p className="text-xs text-[#2c382f]/80 leading-relaxed">{d.bio}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#12281a] text-[#f7f5f0] text-center" data-testid="home-cta-section">
        <div className="max-w-3xl mx-auto px-6">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.25em] text-[#b38b47] font-semibold block mb-4">
              Executive Desk
            </span>
            <h2 className="text-3xl sm:text-4xl font-monument mb-6">Begin the Conversation</h2>
            <p className="text-sm text-[#f7f5f0]/70 mb-10">
              Project enquiries, landowner partnerships and investor conversations — our leadership desk responds directly.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                data-testid="home-cta-contact-button"
                className="px-8 py-3.5 bg-[#b38b47] hover:bg-[#9a7638] text-[#12281a] text-xs uppercase tracking-widest font-bold transition-colors"
              >
                Contact Us
              </Link>
              <Link
                to="/legacy"
                data-testid="home-cta-journey-button"
                className="px-8 py-3.5 border border-[#f7f5f0]/30 hover:border-[#b38b47] text-[#f7f5f0] text-xs uppercase tracking-widest font-semibold transition-colors"
              >
                Explore Our Journey
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
